import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { AlertsService } from '../../services/alerts.service';
import { ViewerService } from '../../services/viewer.service';
import { formatString } from '../../utils/string-utils';
import { DocViewerFactoryService } from '../../services/factory/doc-viewer-factory.service';
import { AddPagesModalComponent } from '../add-pages-modal/add-pages-modal.component';

export interface Page {
	pageNum: number;
	ammount?: number;
	sheet?: { sheetNum: number, amount: number }[];
	overlay?: { overlayNum: number, amount: number }[];
	type: string;
	name?: string;
	fileType?: string;
}
export interface Cart {
	id: number;
	title?: string;
	pages: Page[];
	uuid?: string | null;
	amount?: number;
}
export interface PageResult {
	main: Page;
	sheet?: { sheetNum: number };
	overlay?: { overlayNum: number };
}

export interface ItemsCart {
	id: number;
	title?: string;
	pages: CartItem[];
	uuid?: string | null;
	amount?: number;
}
export interface CartItem {
	key: string;
	type: string;
	_thumbs: string;
	_overview: string;
	title: string;
	amount: number;
	pageNum: number;
	overlayNum?: number;
	sheetNum?: number;
	name?: string;
	fileType?: string;
}

@Injectable({
	providedIn: 'root'
})
export class CartService {

	constructor(
		private configService: ConfigService,
		private http: HttpClient,
		private ngbModal: NgbModal,
		private userService: UserService,
		private alertsService: AlertsService,
		private viewerService: ViewerService,
		private docViewerFactory: DocViewerFactoryService
	) {
		this.initializeCart();
		this.userService.broadcastEvent.subscribe((eventId) => {
			if (eventId === "loggedin") {
				this.initializeCart(); // Call initializeCart when the event is emitted
			} else if (eventId === "loggedout") {
				this.cart = {};
				localStorage.removeItem('cart');
				this.updateCart();
			}
		});
	}

	cart: { [key: string]: Cart } = {};
	itemsCart: { [key: string]: ItemsCart } = {};
	count: number = 0;
	totalAmount: number = 0;
	broadcastEvent: EventEmitter<string> = new EventEmitter();
	expandedKeys: string | null = null;

	async downloadDocument(id: string, title: string, pages: any): Promise<ArrayBuffer> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json'
		});

		const requestBody = [{ id: id, title: this.normalizeTitle(title), uuid: null, pages: pages }];

		try {
			const response = await firstValueFrom(this.http.put<ArrayBuffer>(
				this.configService.getConfig().document.documentDownloadURL, requestBody,
				{ headers, responseType: 'arraybuffer' as 'json' }
			));

			if (!response) {
				throw new Error('No data received'); // Handle undefined response
			}

			return response;
		} catch (error) {
			console.error('Download failed', error);
			throw error; // Re-throw to let the caller handle it
		}
	}

	getPageFromPages(
		pages: Page[] | null,
		id: number,
		pageNum: number,
		type: "sheet" | "overlay",
		secondaryNumber: number
	): PageResult | null {
		if (!pages) {
			return null;
		}

		for (const currPage of pages) {
			if (secondaryNumber > -1 && currPage.pageNum === pageNum) {
				if (type === "sheet" && currPage.sheet) {
					const sheet = currPage.sheet.find(value => value.sheetNum === secondaryNumber);
					if (sheet) {
						return { main: currPage, sheet: sheet };
					}
				}
				if (type === "overlay" && currPage.overlay) {
					const overlay = currPage.overlay.find(value => value.overlayNum === secondaryNumber);
					if (overlay) {
						return { main: currPage, overlay: overlay };
					}
				}
			} else if (currPage.pageNum === pageNum) {
				return { main: currPage };
			}
		}

		return null;
	}

	appendPage(id: number, title: string, pageInfo: any): void {
		let page = pageInfo.main;
		let sheet = pageInfo.sheet;
		let overlay = pageInfo.overlay;

		// Handle sheet or overlay initialization
		if (sheet) {
			page.sheet = [sheet];
			page.overlay = [];
		} else if (overlay) {
			page.overlay = [overlay];
			page.sheet = [];
		}

		// Initialize cart if it doesn't exist
		if (!this.cart[id]) {
			this.cart[id] = { id: id, title: this.normalizeTitle(title), pages: [] };
		}

		let pageAppended = false;
		// Loop through existing pages to check if the page already exists
		for (let p = 0; p < this.cart[id].pages.length; p++) {
			let currPage = this.cart[id].pages[p];
			if (currPage.pageNum === pageInfo.main.pageNum) {
				currPage.ammount = pageInfo.main.ammount;
				if (sheet) {
					if (!currPage.sheet) {
						currPage.sheet = []; // Initialize as an empty array if undefined
					}
					currPage.sheet.push(sheet);
					currPage.sheet.sort((a: any, b: any) => a.sheetNum > b.sheetNum ? 1 : b.sheetNum > a.sheetNum ? -1 : 0);
					pageAppended = true;
				} else if (overlay) {
					if (!currPage.overlay) {
						currPage.overlay = []; // Initialize as an empty array if undefined
					}
					currPage.overlay.push(overlay);
					currPage.overlay.sort((a: any, b: any) => a.overlayNum > b.overlayNum ? 1 : b.overlayNum > a.overlayNum ? -1 : 0);
					pageAppended = true;
				}
			}
		}

		// If the page was not found, add it to the cart
		if (!pageAppended) {
			this.cart[id].pages.push(page);
			this.cart[id].pages.sort((a: Page, b: Page) => a.pageNum > b.pageNum ? 1 : b.pageNum > a.pageNum ? -1 : 0);
		}
	}

	// Optionally, get the cart if needed
	getCart(id: string): Cart | undefined {
		return this.cart[id];
	}

	// TODA check if it page.pageNum
	addPageToCart(id: number, title: string, page: any): void {
		const pageNumber = page.pageNumber;
		const type = page.type;
		const secondaryNumber = page.secondaryNumber;
		this.viewerService.getDocumentInfo(id).then((result: any) => {
			const pages = (result.documentPages) ? result.documentPages : result?.data?.documentPages;
			if (
				this.cart[id] &&
				this.getPageFromPages(this.cart[id].pages, id, pageNumber, type, secondaryNumber)
			) {
				this.alertsService.error({
					title: 'Cart Error',
					message: 'Page is already in the cart.',
				});
				return;
			}

			const updatedPageInfo = this.getPageFromPages(pages, id, pageNumber, type, secondaryNumber);
			this.appendPage(id, this.normalizeTitle(title), updatedPageInfo);
			this.updateCart();

			this.alertsService.success({
				title: 'Cart Updated',
				message: 'Page added to cart.',
			});
		}).catch((error: any) => {
			console.error('Error fetching document info:', error);
		}
		);

	}

	updateCart(): void {
		let count = 0;
		let totalAmount = 0;
		this.itemsCart = {};
		// Loop through documents in _cart
		for (const docId in this.cart) {
			const document = this.cart[docId];
			const currDoc: CartItem[] = [];
			let amount = 0;

			// Process pages in the document
			for (let i = 0; i < document.pages.length; i++) {
				const page = document.pages[i];
				let currPageCount = 0;

				// Process overlays
				if (page.overlay && page.overlay.length > 0) {
					for (let p = 0; p < page.overlay.length; p++) {
						const overlay = page.overlay[p];
						currDoc.push({
							key: `${docId}|${page.pageNum}|overlay|${overlay.overlayNum}`,
							type: 'overlay',
							_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, overlay.overlayNum, 'OVERLAY'),
							_overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, overlay.overlayNum, 'OVERLAY'),
							title: `Page ${page.pageNum} Overlay ${overlay.overlayNum}`,
							overlayNum: overlay.overlayNum,
							amount: overlay.amount,
							pageNum: page.pageNum
						});
						count++;
						amount += overlay.amount;
					}
				}

				// Process sheets
				if (page.sheet && page.sheet.length > 0) {
					for (let p = 0; p < page.sheet.length; p++) {
						const sheet = page.sheet[p];
						currDoc.push({
							key: `${docId}|${page.pageNum}|sheet|${sheet.sheetNum}`,
							_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, sheet.sheetNum, 'STANDARD'),
							_overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, sheet.sheetNum, 'STANDARD'),
							type: 'sheet',
							title: `Page ${page.pageNum} Sheet ${sheet.sheetNum}`,
							sheetNum: sheet.sheetNum,
							amount: sheet.amount,
							pageNum: page.pageNum
						});
						count++;
						amount += sheet.amount;
					}
				}

				// Process standard pages
				if ((!page.sheet || page.sheet.length === 0) && (!page.overlay || page.overlay.length === 0)) {
					let title = `Page ${page.pageNum}`;
					if (page.type === 'INDEX_PAGE') {
						title = 'Index Page';
					}
					if (page.type === 'STATUS_PAGE') {
						title = 'Status Page';
					}

					currDoc.push({
						key: `${docId}|${page.pageNum}|standard`,
						_thumbs: formatString(this.configService.getConfig().document.thumbnailPageURL, document.id, page.pageNum, 0, 'STANDARD'),
						_overview: formatString(this.configService.getConfig().document.overviewPageURL, document.id, page.pageNum, 0, 'STANDARD'),
						type: page.type,
						title: title,
						amount: page.ammount ?? 0,
						pageNum: page.pageNum ?? 0
					});
					count++;
					amount += page.ammount ?? 0;
				}
			}

			// Update cart for the document
			this.itemsCart[docId] = { id: Number(docId), pages: currDoc, title: document.title, amount: amount };
			totalAmount += amount;
		}

		this.totalAmount = totalAmount;
		this.count = count;

		// Save cart if user is logged in
		if (this.userService?.user?.loggedin) {
			this.saveUsersCart(this.cart).then(() => {
				// Emit cart updated event
				this.broadcastEvent.emit('cartUpdated');
			});
		} else {
			localStorage.setItem('cart', JSON.stringify(this.cart));
			// Emit cart updated event
			this.broadcastEvent.emit('cartUpdated');
		}
	}

	// Get the user's cart from the backend using a Promise
	getUsersCart(): Promise<Cart[]> {
		const url = this.configService.getConfig().auth.getCartURL;
		return firstValueFrom(this.http.get<any>(url))
			.catch(this.handleError); // Handle errors using a catch method
	}

	// Save the user's cart to the backend using a Promise
	saveUsersCart(cart: any): Promise<any> {
		const allObjects = Object.keys(cart).map(cartName => ({
			title: cart[cartName].title,
			id: cart[cartName].id,
			pages: cart[cartName].pages,
			amount: cart[cartName].amount
		}));

		const url = this.configService.getConfig().auth.saveCartURL;
		return firstValueFrom(this.http.put<any>(url, allObjects))
			.catch(this.handleError); // Handle errors using a catch method
	}
	// Handle HTTP errors (logging or rethrowing)
	private handleError(error: any): Promise<never> {
		console.error('An error occurred:', error);
		return Promise.reject(error); // Return a rejected promise
	}

	// Add a document to the cart
	addToCart(id: number, title: string): void {
		this.viewerService.getDocumentInfo(id)
			.then((result: any) => {
				const pages = result.documentPages || [];

				// Check if the document is already in the cart
				if (this.cart[id] && pages.length === this.cart[id].pages.length) {
					this.alertsService.error({
						title: 'Cart Error',
						message: 'Document is already in the cart.'
					});
					return;
				}

				if(pages && pages[1] && pages[1].fileType?.includes("Additional Page")){
					const modalRef = this.ngbModal.open(AddPagesModalComponent);
					modalRef.result.then(
				      (number) => {
				        console.log('User selected pages:', number);
				        // Add to cart logic here
				        try{
                            let num = parseInt(number);
                            if(num && num > 0){
                                let  ros_pages = [];
                                for(let i = 0; i < num; i++){
                                    if(i == 0){
                                        ros_pages.push(pages[0]);
                                    }else{
                                        ros_pages.push(pages[1]);
                                    }
                                }
                                this.addPagesToCart(id, title, ros_pages);
                            }

                        }catch(e){
                            this.alertsService.error({
								title: 'Cart Update Failed',
								message: `Invalid page number.`
							});
                        }
				      },
				      (reason) => {
				        console.log('Modal dismissed:', reason);
				      }
				    );
				} else {
					this.addPagesToCart(id, title, pages);
				}
			})
			.catch((error: any) => {
				console.error('Error adding to cart:', error);
			});
	}
	
	addPagesToCart(id: number, title: string, pages: Page[]){
        this.cart[id] = { id: id, title: title, uuid: null, pages: pages };
		// Update cart
		this.updateCart();
		
        let count = 0;
		pages.forEach((currPage: Page) => {
			count += currPage.sheet?.length || 0;
			count += currPage.overlay?.length || 0;

			if ((!currPage.overlay || currPage.overlay.length === 0) &&
				(!currPage.sheet || currPage.sheet.length === 0)) {
				count++;
			}
		});

		// Show success message
		this.alertsService.success({
			title: 'Cart Updated',
			message: `${count} Page(s) added to cart.`
		});
    }

	samePage(pageA: any, pageB: any) {
		//assumes same ID;
		let pageAString = JSON.stringify(pageA)
		let pageBString = JSON.stringify(pageB)
		if (pageAString == pageBString) {
			return true;
		};

		return false;
	};

	// Add a page to the cart
	addPage(id: number, documentPage: any): void {
		if (this.cart[id]) {
			const docCart = this.cart[id];

			// Check if the page already exists in the cart
			if (docCart.pages.some((page: any) => this.samePage(page, documentPage))) {
				this.alertsService.error({
					title: 'Cart Error',
					message: 'Page is already in the cart.'
				});
				return;
			}

			// Add new page to the document's cart entry
			docCart.pages.push(documentPage);
		} else {
			// Create a new entry if the document is not in the cart
			this.cart[id] = { id: id, uuid: null, pages: [documentPage] };
		}

		// Update the cart
		this.updateCart();
	}

	removeDocument(id: string) {
		if (this.cart[id]) {
			delete this.cart[id];
			this.updateCart();
		};
	};

	removePage(info: string): void {
		const key = info.split("|");
		const inputID = key[0];     // cart ID
		const inputPage = Number(key[1]);   // Page number
		const inputType = key[2];   // Type: standard, sheet, or overlay

		const document = this.cart[inputID];

		if (document) {
			// Find the index of the page in the pages array
			const pageIndex = document.pages.findIndex(page => page.pageNum === inputPage);

			if (pageIndex !== -1) {
				const page = document.pages[pageIndex];

				if (inputType === "standard") {
					// Remove the page if it's a standard type
					document.pages.splice(pageIndex, 1);
				} else if (inputType === "sheet") {
					const sheetNum = Number(key[3]);
					if (page.sheet) {
						page.sheet = page.sheet.filter(sheet => sheet.sheetNum !== sheetNum);
					}
				} else if (inputType === "overlay") {
					const overlayNum = Number(key[3]);
					if (page.overlay) {
						page.overlay = page.overlay.filter(overlay => overlay.overlayNum !== overlayNum);
					}
				}

				// If the page has no sheets or overlays left, remove it?
				if ((!page.sheet || page.sheet.length === 0) && (!page.overlay || page.overlay.length === 0)) {
					//document.pages.splice(pageIndex, 1);
				}
			}

			// If the document has no more pages, remove it from the cart
			if (document.pages.length === 0) {
				delete this.cart[inputID];
			}
		}

		this.updateCart(); // Refresh the cart after removal
	}


	initializeCart(): void {
		// Ensure user object exists before checking `loggedin`
		if (this.userService?.user?.loggedin) {
			// Retrieve user's cart from the server
			this.getUsersCart()
				.then((response: Cart[]) => {
					const cartItems = response ?? [];
					const cart: { [key: string]: Cart } = {};

					// Process server cart data
					cartItems.forEach((item) => {
						if (item?.id) {
							cart[item.id] = item;
						}
					});

					// Assign to the service's cart
					this.cart = cart;

					// If there's a local storage cart, merge it into the user's cart
					const sessionCart = this.loadSessionCart();
					if (sessionCart && typeof sessionCart === "object") {
						Object.keys(sessionCart).forEach((doc) => {
							if (!this.cart[doc]) {
								this.cart[doc] = sessionCart[doc];
							}
						});
					}

					// Clear the localStorage cart after merge
					localStorage.removeItem("cart");

					// Update the cart UI
					this.updateCart();
				})
				.catch((error: any) => {
					console.error("Error fetching cart:", error);
					this.cart = {}; // Reset cart on failure
				});
		} else {
			// If not logged in, load the session cart safely
			this.cart = this.loadSessionCart() || {};
			this.updateCart();
		}
	}

	getExpandedKeys(): string {
		return this.expandedKeys ?? '';
	}

	setExpandedKeys(expandedKeys: string): void {
		this.expandedKeys = expandedKeys;
		this.broadcastEvent.emit('expandedKeysUpdated');
	}

	showDocument(id: string): void {
		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});

		modalRef.componentInstance.productId = id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	showPage(key: string | undefined): void {
		if (key) {
			const [id, pageNumber, type, secNum] = key.split('|');

			const viewerComponent = this.docViewerFactory.getViewerComponent();
			const modalRef = this.ngbModal.open(viewerComponent, {
				windowClass: "viewer-modal-window"
			});
			
			modalRef.componentInstance.productId = id;
			modalRef.componentInstance.pageInfo = {
				pageNum: pageNumber,
				type: type,
				secNum: secNum
			};

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	// Helper method to safely parse localStorage
	private loadSessionCart(): { [key: string]: any } {
		try {
			const cartData = localStorage.getItem('cart');
			return cartData ? JSON.parse(cartData) : {};
		} catch (e) {
			console.error('Error loading cart from localStorage', e);
			return {};
		}
	}

	private normalizeTitle(title: string): string {
		if (title) {
			title = title.replace('<mark>', '').replace('</mark>', '');
		}
		return title;
	}
	clearCart(): void {
		this.cart = {};
		this.updateCart();
	};
	runBroadcastEvent(eventId: string) {
		this.broadcastEvent.emit(eventId);
	}
}
